/**
 * Search and results — the primary screen.
 *
 * Mobile structure (spec section 27): header → search → goal → sort/filter →
 * results → comparison tray. On desktop the filters move into a left sidebar and
 * the charts sit beside the results (section 28), from the same component tree.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ApiError, searchProducts, type ApiMeta } from '@/api/client';
import { computeMetrics } from '@/lib/nutrition/calculations';
import {
  analyse,
  applyFilters,
  buildFacets,
  buildInsights,
  countActiveFilters,
  emptyFilters,
  sortOptionById,
  type Filters,
} from '@/lib/nutrition/ranking';
import { goalById, resolveGoalMetric } from '@/lib/nutrition/scoring';
import { SORT_OPTIONS } from '@/lib/nutrition/ranking';
import type { Product } from '@/types/product';
import { useAppState } from '@/state/AppState';
import { GoalSelector, SearchBar, SortControl } from '@/components/Controls';
import { FilterSheet } from '@/components/FilterSheet';
import { ProductCard } from '@/components/ProductCard';
import { InsightList, Notice, ProvenanceLine } from '@/components/Panels';
import { StoreCoverage } from '@/components/StoreCoverage';
import { RankingBarChart } from '@/components/charts/RankingBarChart';
import { ValueScatterChart, type ScatterAxis } from '@/components/charts/ValueScatterChart';

export function SearchPage(): React.JSX.Element {
  const [params, setParams] = useSearchParams();
  const query = params.get('q') ?? '';
  const { goal, setGoal, sortId, setSortId, compareIds, toggleCompare, compareFull, isComparing } =
    useAppState();

  const [products, setProducts] = useState<Product[]>([]);
  const [meta, setMeta] = useState<ApiMeta | null>(null);
  const [fromCache, setFromCache] = useState(false);
  const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<Filters>(emptyFilters);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [scatterAxis, setScatterAxis] = useState<ScatterAxis>('protein');

  // Selecting a goal picks the sort metric for you (section 15). The chosen
  // metric is always displayed, and the sort dropdown stays free to override.
  const applyGoal = useCallback(
    (nextGoal: typeof goal) => {
      setGoal(nextGoal);
      const metric = resolveGoalMetric(goalById(nextGoal), null);
      const option =
        metric === null
          ? SORT_OPTIONS.find((o) => o.metric === 'valueScore')
          : SORT_OPTIONS.find((o) => o.metric === metric);
      if (option) setSortId(option.id);
    },
    [setGoal, setSortId],
  );

  useEffect(() => {
    if (query.trim().length === 0) {
      setProducts([]);
      setMeta(null);
      setStatus('idle');
      return;
    }

    const controller = new AbortController();
    setStatus('loading');
    setError(null);

    searchProducts({ query, limit: 20 }, controller.signal)
      .then((result) => {
        setProducts(result.data.products);
        setMeta(result.meta);
        setFromCache(result.fromCache);
        setStatus('idle');
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setStatus('error');
        setError(err instanceof ApiError ? err.message : 'Search failed. Please try again.');
      });

    return () => controller.abort();
  }, [query]);

  const sort = sortOptionById(sortId);

  // One analysis pass feeds the list, the charts and the insights, so they can
  // never disagree about a number.
  const analysed = useMemo(() => analyse(products, computeMetrics, sort), [products, sort]);
  const facets = useMemo(() => buildFacets(analysed), [analysed]);
  const visible = useMemo(() => applyFilters(analysed, filters), [analysed, filters]);
  const insights = useMemo(() => buildInsights(visible, sort), [visible, sort]);

  const trayNames = useMemo(
    () =>
      compareIds.map((id) => ({
        id,
        name: products.find((p) => p.id === id)?.name ?? id,
      })),
    [compareIds, products],
  );
  void trayNames;

  const onSearch = (q: string): void => {
    setParams(q.trim().length > 0 ? { q: q.trim() } : {});
    setFilters(emptyFilters());
  };

  const charts = (
    <>
      <section className="panel">
        <h2 className="panel-title">Value ranking</h2>
        <RankingBarChart rows={visible} metric={sort.metric} maxBars={8} />
      </section>

      <section className="panel">
        <div className="sheet-head">
          <h2 className="panel-title" style={{ marginBottom: 0 }}>
            Price vs. nutrition
          </h2>
          <div style={{ display: 'flex', gap: '0.25rem' }}>
            <button
              type="button"
              className="chip"
              aria-pressed={scatterAxis === 'protein'}
              onClick={() => setScatterAxis('protein')}
            >
              Protein
            </button>
            <button
              type="button"
              className="chip"
              aria-pressed={scatterAxis === 'kcal'}
              onClick={() => setScatterAxis('kcal')}
            >
              Calories
            </button>
          </div>
        </div>
        <ValueScatterChart rows={visible} axis={scatterAxis} />
      </section>

      <section className="panel">
        <h2 className="panel-title">Price per 100 g / 100 ml</h2>
        <RankingBarChart rows={visible} metric="pricePer100g" maxBars={8} />
      </section>
    </>
  );

  return (
    <>
      {/*
        The way back.

        Search is no longer a tab, so nothing in the header marks where you are
        or how to leave. This does both: it names the place you came from, and
        it is the first thing in the reading order rather than something to hunt
        for after the results.
      */}
      <Link className="back-link" to="/">
        <span className="back-link-arrow" aria-hidden="true">
          ←
        </span>
        Rankings
      </Link>

      <SearchBar value={query} onSearch={onSearch} busy={status === 'loading'} />

      {query.trim().length === 0 ? (
        <div className="empty-state">
          <h2>Which product gives you the most value?</h2>
          <p>
            Search for a food product to compare protein per dollar, calories per dollar, price per
            100&nbsp;g and more — across stores.
          </p>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
            This tool measures nutritional value for money. It does not tell you whether a food is
            healthy.
          </p>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
            Or <Link to="/">browse the rankings</Link> to see the best of the whole catalogue.
          </p>
        </div>
      ) : (
        <>
          <div style={{ marginTop: '0.75rem' }}>
            <GoalSelector goal={goal} onChange={applyGoal} activeSortLabel={sort.label} />
          </div>

          <SortControl
            sortId={sortId}
            onChange={setSortId}
            onOpenFilters={() => setSheetOpen(true)}
            activeFilterCount={countActiveFilters(filters)}
            resultCount={visible.length}
          />

          {status === 'error' && (
            <Notice kind="error" title="Search failed.">
              {error}
            </Notice>
          )}

          {meta && meta.warnings.length > 0 && (
            <Notice kind="info" title="About this data.">
              <ul>
                {meta.warnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            </Notice>
          )}

          <StoreCoverage />

          <div className="layout">
            <aside className="layout-sidebar desktop-only">
              <FilterSheet
                filters={filters}
                facets={facets}
                onChange={setFilters}
                variant="inline"
                resultCount={visible.length}
              />
            </aside>

            <div>
              <InsightList insights={insights} />

              {status === 'loading' && products.length === 0 ? (
                <div className="results">
                  {[0, 1, 2, 3].map((i) => (
                    <div className="skeleton" key={i} />
                  ))}
                </div>
              ) : visible.length === 0 ? (
                <div className="empty-state">
                  <h2>No matching products</h2>
                  <p>
                    {analysed.length > 0
                      ? 'Your filters excluded every result. Try relaxing them.'
                      : 'Nothing was found for this search. Try a different term.'}
                  </p>
                </div>
              ) : (
                <div className="results">
                  {visible.map((row) => (
                    <ProductCard
                      key={row.product.id}
                      row={row}
                      primaryMetric={sort.metric}
                      isComparing={isComparing(row.product.id)}
                      compareDisabled={compareFull}
                      onToggleCompare={toggleCompare}
                    />
                  ))}
                </div>
              )}

              {meta && (
                <div style={{ marginTop: '0.75rem' }}>
                  <ProvenanceLine
                    source={meta.source}
                    generatedAt={meta.generatedAt}
                    cached={meta.cached}
                    fromCache={fromCache}
                    isSampleData={meta.isSampleData}
                  />
                </div>
              )}
            </div>

            <aside className="layout-aside">{visible.length > 0 && charts}</aside>
          </div>
        </>
      )}

      <div className="mobile-only">
        <FilterSheet
          filters={filters}
          facets={facets}
          onChange={setFilters}
          variant="sheet"
          open={sheetOpen}
          onClose={() => setSheetOpen(false)}
          resultCount={visible.length}
        />
      </div>
    </>
  );
}
